import { useEffect, useState } from 'react';
import { editionsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ImageUpload } from '../../components/ui/image-upload';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Calendar, Image as ImageIcon } from 'lucide-react';

export const EditionsAdminPage = () => {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEdition, setEditingEdition] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    year: new Date().getFullYear(),
    description: '',
    hero_image: '',
    status: 'draft',
  });

  useEffect(() => {
    loadEditions();
  }, []);

  const loadEditions = async () => {
    try {
      const res = await editionsAPI.getAll();
      setEditions(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle edizioni');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      year: new Date().getFullYear(),
      description: '',
      hero_image: '',
      status: 'draft',
    });
    setEditingEdition(null);
  };

  const openEditDialog = (edition) => {
    setEditingEdition(edition);
    setFormData({
      title: edition.title,
      year: edition.year,
      description: edition.description || '',
      hero_image: edition.hero_image || '',
      status: edition.status,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEdition) {
        await editionsAPI.update(editingEdition.id, formData);
        toast.success('Edizione aggiornata');
      } else {
        await editionsAPI.create(formData);
        toast.success('Edizione creata');
      }
      setDialogOpen(false);
      resetForm();
      loadEditions();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questa edizione?')) return;
    try {
      await editionsAPI.delete(id);
      toast.success('Edizione eliminata');
      loadEditions();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div className="space-y-6" data-testid="editions-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Edizioni</h1>
          <p className="text-muted-foreground">Gestisci le edizioni SIOS</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-edition-btn">
              <Plus size={18} className="mr-2" />
              Nuova Edizione
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingEdition ? 'Modifica Edizione' : 'Nuova Edizione'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Titolo</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="SIOS 2025"
                  className="bg-white/5 border-white/10"
                  required
                  data-testid="input-title"
                />
              </div>

              <div className="space-y-2">
                <Label>Anno</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="bg-white/5 border-white/10"
                  required
                  data-testid="input-year"
                />
              </div>

              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione dell'edizione..."
                  className="bg-white/5 border-white/10 min-h-24"
                  data-testid="input-description"
                />
              </div>

              <ImageUpload
                value={formData.hero_image}
                onChange={(url) => setFormData({ ...formData, hero_image: url })}
                label="Immagine Hero"
              />

              <div className="space-y-2">
                <Label>Stato</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10" data-testid="select-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="publish">Pubblicato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="submit-edition-btn">
                  {editingEdition ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <div className="glass rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="w-16">Hero</TableHead>
              <TableHead>Titolo</TableHead>
              <TableHead>Anno</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {editions.map((edition) => {
              const heroUrl = edition.hero_image 
                ? (edition.hero_image.startsWith('http') ? edition.hero_image : `${process.env.REACT_APP_BACKEND_URL}${edition.hero_image}`)
                : null;
              
              return (
                <TableRow key={edition.id} className="border-white/5" data-testid={`edition-row-${edition.id}`}>
                  <TableCell>
                    {heroUrl ? (
                      <img 
                        src={heroUrl} 
                        alt={edition.title} 
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center">
                        <ImageIcon size={16} className="text-muted-foreground" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{edition.title}</TableCell>
                  <TableCell>{edition.year}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      edition.status === 'publish' 
                        ? 'bg-green-500/20 text-green-500' 
                        : 'bg-yellow-500/20 text-yellow-500'
                    }`}>
                      {edition.status === 'publish' ? 'Pubblicato' : 'Bozza'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDialog(edition)}
                        data-testid={`edit-edition-${edition.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(edition.id)}
                        data-testid={`delete-edition-${edition.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}

            {editions.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Calendar className="mx-auto text-muted-foreground mb-4" size={32} />
                  <p className="text-muted-foreground">Nessuna edizione trovata</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
